import React, { useEffect, useState } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import { Eye, Glasses, Sun, Wrench, Contact, Heart, Phone, Calendar } from 'lucide-react';
import YearsPopup from '../components/YearsPopup';

const BACKEND_URL = '';

const iconMap = {
  Eye: Eye,
  Glasses: Glasses,
  Sun: Sun,
  Wrench: Wrench,
  Contact: Contact,
  Heart: Heart
};

const ServicesPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  // Show popup every time user visits the services page
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only scroll to top if no hash in URL
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
    
    // Fetch services from API
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/services`);
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [location.hash]);

  useEffect(() => {
    // Handle scroll to specific service from URL hash or query param
    if (!loading && services.length > 0) {
      const hash = location.hash.replace('#', '');
      const serviceParam = searchParams.get('service');
      const targetService = hash || serviceParam;
      
      if (targetService) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(`service-${targetService}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add highlight effect
            element.classList.add('ring-4', 'ring-red-500', 'ring-offset-4');
            setTimeout(() => {
              element.classList.remove('ring-4', 'ring-red-500', 'ring-offset-4');
            }, 2000);
          }
        }, 300);
      }
    }
  }, [location, searchParams, loading, services]);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* 52 Years Popup */}
      <YearsPopup isOpen={showPopup} onClose={handleClosePopup} />

      {/* Main Hero Section */}
      <section className="relative bg-gray-900 text-white min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_929915db-def1-4d81-a6ce-06dd0e5d42cf/artifacts/85bkfia8_ali-pazani-cDGaKsbgIwo-unsplash.jpg"
            alt="Eye care services"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 30%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">Our Eye Care Services</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto mb-8">
            Comprehensive solutions tailored to your needs with over 52 years of trusted experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 text-lg"
            >
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Link>
            <a
              href="tel:(289) 652-1682"
              className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium flex items-center justify-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What We Offer</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click on any service to learn more about how we can help you
            </p>
          </div>

          {/* Services Grid - Square Boxes */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              const serviceSlug = service.title.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '');
              
              return (
                <Link
                  key={service.id}
                  id={`service-${serviceSlug}`}
                  to={`/services/${serviceSlug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {/* Service Image */}
                  <div className="aspect-square relative overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=600&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                      {Icon && <Icon className="w-6 h-6 text-white" />}
                    </div>
                    
                    {/* Title Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                        {service.title}
                      </h3>
                      <p className="text-white/90 text-sm line-clamp-2">
                        {service.description}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-red-400 font-medium group-hover:text-red-300 transition-colors">
                        <span>Learn More</span>
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Streetsville Eyecare?</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold">52+</span>
              </div>
              <h3 className="font-bold text-xl mb-2">Years Experience</h3>
              <p className="text-white font-medium">Trusted eye care since 1974</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8" />
              </div>
              <h3 className="font-bold text-xl mb-2">Professional Care</h3>
              <p className="text-white font-medium">Licensed optometrists on staff</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">100+</span>
              </div>
              <h3 className="font-bold text-xl mb-2">Designer Brands</h3>
              <p className="text-white font-medium">Boutique frames from around the world</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-block bg-white text-red-600 px-10 py-4 rounded-lg hover:bg-red-50 transition-colors font-bold text-lg"
            >
              Book Your Appointment Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
